import { useState, useEffect, useMemo, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, TrendingUp, X, AlertCircle, Building2, User, CheckCircle2, GitBranch, Users, ExternalLink } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import DuplicateConfirmDialog from "@/components/DuplicateConfirmDialog";
import AnimationVariantPicker from "@/components/AnimationVariantPicker";
import AnimationCatalogCombobox from "@/components/AnimationCatalogCombobox";
import useDuplicateConfirm from "@/hooks/useDuplicateConfirm";
import { useAuth } from "@/contexts/AuthContext";

// Helper: convert any date format (YYYY-MM-DD, DD/MM/YYYY, French) to YYYY-MM-DD
const parseDateToISO = (dateStr) => {
  if (!dateStr) return "";
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) return dateStr;
  const slashMatch = dateStr.match(/(\d{1,2})\/(\d{1,2})\/(\d{4})/);
  if (slashMatch) return `${slashMatch[3]}-${slashMatch[2].padStart(2,'0')}-${slashMatch[1].padStart(2,'0')}`;
  const months = {janvier:'01',fevrier:'02',février:'02',mars:'03',avril:'04',mai:'05',juin:'06',juillet:'07',aout:'08',août:'08',septembre:'09',octobre:'10',novembre:'11',decembre:'12',décembre:'12'};
  const frMatch = dateStr.toLowerCase().match(/(\d{1,2})\s+(janvier|fevrier|février|mars|avril|mai|juin|juillet|aout|août|septembre|octobre|novembre|decembre|décembre)\s+(\d{4})/);
  if (frMatch) return `${frMatch[3]}-${months[frMatch[2]]}-${frMatch[1].padStart(2,'0')}`;
  const d = new Date(dateStr);
  if (!isNaN(d.getTime())) return d.toISOString().split('T')[0];
  return "";
};

const OpportunityForm = ({ extractedData, animations, apiUrl, existingCompany, existingContact, createdContact, createdCompany, createdOpportunity, onOpportunityCreated, onUpdateExtractedData }) => {
  const [loading, setLoading] = useState(false);
  const { user } = useAuth();

  // Raw CRM lists (from API only)
  const [companiesList, setCompaniesList] = useState([]);
  const [contactsList, setContactsList] = useState([]);
  const [funnels, setFunnels] = useState([]);
  const [steps, setSteps] = useState([]);
  const [staff, setStaff] = useState([]);

  // Selections
  const [selectedCompanyId, setSelectedCompanyId] = useState("");
  const [selectedContactId, setSelectedContactId] = useState("");
  const [selectedFunnelId, setSelectedFunnelId] = useState("");
  const [selectedStepId, setSelectedStepId] = useState("");
  const [selectedStaffId, setSelectedStaffId] = useState("");

  // Effective existing entities: prioritise what was just created/selected via the
  // duplicate dialog over what the initial extraction detected. This is what
  // makes "Utiliser celui-ci" from ContactForm correctly propagate here.
  const effectiveExistingContact = useMemo(() => {
    if (createdContact?.sellsy_id || createdContact?.id) {
      return {
        id: createdContact.sellsy_id || createdContact.id,
        sellsy_id: createdContact.sellsy_id || createdContact.id,
        name: createdContact.name || `${createdContact.first_name || ''} ${createdContact.last_name || ''}`.trim(),
        email: createdContact.email,
      };
    }
    return existingContact;
  }, [createdContact, existingContact]);

  const effectiveExistingCompany = useMemo(() => {
    if (createdCompany?.sellsy_id || createdCompany?.id) {
      return {
        id: createdCompany.sellsy_id || createdCompany.id,
        sellsy_id: createdCompany.sellsy_id || createdCompany.id,
        name: createdCompany.name,
      };
    }
    return existingCompany;
  }, [createdCompany, existingCompany]);

  // Virtual entries (new contact/company not yet in Sellsy) — computed from extractedData
  const virtualContact = useMemo(() => {
    if (effectiveExistingContact?.sellsy_id) return null;
    if (!extractedData?.email) return null;
    return {
      id: `new_contact_${extractedData.email}`,
      first_name: extractedData.first_name || "",
      last_name: extractedData.last_name || "",
      email: extractedData.email,
      isVirtual: true,
    };
  }, [extractedData, effectiveExistingContact]);

  const virtualCompany = useMemo(() => {
    if (effectiveExistingCompany?.sellsy_id) return null;
    if (!extractedData?.company) return null;
    return {
      id: `new_company_${extractedData.company}`,
      name: extractedData.company,
      isVirtual: true,
    };
  }, [extractedData, effectiveExistingCompany]);

  // Merged dropdown lists (virtual entries first)
  const companies = useMemo(() => {
    const list = [...companiesList];
    if (effectiveExistingCompany?.sellsy_id) {
      const found = list.find(c =>
        String(c.sellsy_id) === String(effectiveExistingCompany.sellsy_id) ||
        c.id === effectiveExistingCompany.id
      );
      if (!found) {
        list.unshift({
          id: effectiveExistingCompany.sellsy_id || effectiveExistingCompany.id,
          name: effectiveExistingCompany.name,
          sellsy_id: effectiveExistingCompany.sellsy_id,
        });
      }
    } else if (virtualCompany) {
      list.unshift(virtualCompany);
    }
    return list;
  }, [companiesList, effectiveExistingCompany, virtualCompany]);

  const contacts = useMemo(() => {
    const list = [...contactsList];
    if (effectiveExistingContact?.sellsy_id) {
      const found = list.find(c =>
        String(c.sellsy_id) === String(effectiveExistingContact.sellsy_id) ||
        c.id === effectiveExistingContact.id
      );
      if (!found) {
        const parts = (effectiveExistingContact.name || '').split(' ');
        list.unshift({
          id: effectiveExistingContact.sellsy_id || effectiveExistingContact.id,
          first_name: parts[0] || '',
          last_name: parts.slice(1).join(' ') || '',
          email: effectiveExistingContact.email,
          sellsy_id: effectiveExistingContact.sellsy_id,
        });
      }
    } else if (virtualContact) {
      // Only add virtual if no contact with same email already exists locally
      const found = list.find(c => c.email?.toLowerCase() === virtualContact.email.toLowerCase());
      if (!found) list.unshift(virtualContact);
    }
    return list;
  }, [contactsList, effectiveExistingContact, virtualContact]);

  // Form data — initialized once from extractedData
  const [formData, setFormData] = useState({
    name: extractedData?.company
      ? `${extractedData.company} - ${extractedData.event_date || 'Evenement'}`
      : "Nouvelle opportunite",
    description: extractedData?.request_summary || "",
    amount: extractedData?.budget ? parseFloat(extractedData.budget.replace(/[^\d]/g, '')) || 0 : 0,
    event_date: parseDateToISO(extractedData?.event_date),
    event_end_date: parseDateToISO(extractedData?.event_end_date),
    event_horaires: extractedData?.event_horaires || "",
    event_location: extractedData?.event_location || "",
    participants_count: extractedData?.participants_count || 0
  });
  // Animations are the SINGLE SOURCE OF TRUTH on extractedData — Aperçu and
  // OpportunityForm both read & write the same list. No local state.
  const selectedAnimations = extractedData?.requested_animations || [];
  const updateAnimations = (next) => {
    if (onUpdateExtractedData) {
      onUpdateExtractedData("requested_animations", next);
    }
  };

  // Append selected animations to description (preserves user-edited summary)
  const buildDescription = (baseSummary, animList) => {
    const summary = (baseSummary || "").replace(/\n*\nAnimations selectionnees ?: .*$/s, "").trimEnd();
    if (!animList || animList.length === 0) return summary;
    const animLine = `Animations selectionnees : ${animList.join(", ")}`;
    return summary ? `${summary}\n\n${animLine}` : animLine;
  };

  // Sync formData (NON-animation fields) when extractedData changes
  useEffect(() => {
    if (!extractedData) return;
    setFormData({
      name: extractedData.company
        ? `${extractedData.company} - ${extractedData.event_date || 'Evenement'}`
        : "Nouvelle opportunite",
      description: buildDescription(extractedData.request_summary, extractedData.requested_animations || []),
      amount: extractedData.budget ? parseFloat(extractedData.budget.replace(/[^\d]/g, '')) || 0 : 0,
      event_date: parseDateToISO(extractedData.event_date),
      event_end_date: parseDateToISO(extractedData.event_end_date),
      event_horaires: extractedData.event_horaires || "",
      event_location: extractedData.event_location || "",
      participants_count: extractedData.participants_count || 0
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [extractedData]);

  // Fetch CRM data ONCE on mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [companiesRes, contactsRes, funnelsRes, staffRes] = await Promise.all([
          axios.get(`${apiUrl}/companies`),
          axios.get(`${apiUrl}/contacts`),
          axios.get(`${apiUrl}/sellsy/funnels`),
          axios.get(`${apiUrl}/sellsy/staff`)
        ]);
        setCompaniesList(companiesRes.data || []);
        setContactsList(contactsRes.data || []);
        setFunnels(funnelsRes.data.funnels || []);
        setStaff(staffRes.data.staff || []);
      } catch (e) {
        console.error("Error fetching CRM data:", e);
      }
    };
    fetchData();
  }, [apiUrl]);

  // Auto-select default funnel ("2026" > "Azefir" > first)
  useEffect(() => {
    if (selectedFunnelId || funnels.length === 0) return;
    const azefir2026 = funnels.find(f => f.name?.toLowerCase().includes("2026"));
    const azefir = funnels.find(f => f.name?.toLowerCase().includes("azefir"));
    const defaultFunnel = azefir2026 || azefir || funnels[0];
    if (defaultFunnel) setSelectedFunnelId(defaultFunnel.id);
  }, [funnels, selectedFunnelId]);

  // Fetch steps when funnel changes
  useEffect(() => {
    if (!selectedFunnelId) return;
    const fetchSteps = async () => {
      try {
        const res = await axios.get(`${apiUrl}/sellsy/funnels/${selectedFunnelId}/steps`);
        setSteps(res.data.steps || []);
      } catch (e) {
        console.error("Error fetching steps:", e);
      }
    };
    fetchSteps();
  }, [selectedFunnelId, apiUrl]);

  // Auto-select first step AFTER steps are rendered (avoid Radix Select display race)
  useEffect(() => {
    if (steps.length === 0) return;
    if (selectedStepId && steps.find(s => s.id === selectedStepId)) return;
    setSelectedStepId(steps[0].id);
  }, [steps, selectedStepId]);

  // Auto-select default staff = the Sellsy collaborator whose email matches the
  // logged-in user's email (e.g. projet@azefir.com → projet@azefir.com).
  // If no Sellsy staff matches, leave "Non affecte" (no auto-selection).
  useEffect(() => {
    if (selectedStaffId || staff.length === 0) return;
    const email = (user?.email || "").toLowerCase().trim();
    if (!email) return;
    const mine = staff.find(s => (s.email || "").toLowerCase().trim() === email);
    if (mine) setSelectedStaffId(mine.id);
  }, [staff, selectedStaffId, user]);

  // Auto-select company (runs when list or extracted data changes, ONLY if not already selected to a valid entry)
  useEffect(() => {
    if (selectedCompanyId && companies.find(c => c.id === selectedCompanyId)) return;
    if (companies.length === 0) return;

    // Priority 1: effective existing Sellsy company (createdCompany OR existingCompany)
    if (effectiveExistingCompany?.id || effectiveExistingCompany?.sellsy_id) {
      const match = companies.find(c =>
        c.id === effectiveExistingCompany.id ||
        String(c.sellsy_id) === String(effectiveExistingCompany.sellsy_id) ||
        c.id === String(effectiveExistingCompany.sellsy_id)
      );
      if (match) { setSelectedCompanyId(match.id); return; }
    }
    // Priority 2: virtual company (new, from extractedData)
    if (virtualCompany) {
      const match = companies.find(c => c.id === virtualCompany.id);
      if (match) { setSelectedCompanyId(match.id); return; }
    }
    // Priority 3: name match from extractedData
    if (extractedData?.company) {
      const match = companies.find(c => c.name?.toLowerCase().trim() === extractedData.company.toLowerCase().trim());
      if (match) { setSelectedCompanyId(match.id); return; }
    }
  }, [companies, effectiveExistingCompany, virtualCompany, extractedData, selectedCompanyId]);

  // Auto-select contact (same pattern, with validation against current list)
  useEffect(() => {
    if (selectedContactId && contacts.find(c => c.id === selectedContactId)) return;
    if (contacts.length === 0) return;

    // Priority 1: effective existing Sellsy contact (createdContact OR existingContact)
    if (effectiveExistingContact?.id || effectiveExistingContact?.sellsy_id) {
      const match = contacts.find(c =>
        c.id === effectiveExistingContact.id ||
        String(c.sellsy_id) === String(effectiveExistingContact.sellsy_id) ||
        c.id === String(effectiveExistingContact.sellsy_id)
      );
      if (match) { setSelectedContactId(match.id); return; }
    }
    // Priority 2: email match (works for both locally created contacts and virtual entries)
    if (extractedData?.email) {
      const match = contacts.find(c => c.email?.toLowerCase() === extractedData.email.toLowerCase());
      if (match) { setSelectedContactId(match.id); return; }
    }
    // Priority 3: virtual contact
    if (virtualContact) {
      const match = contacts.find(c => c.id === virtualContact.id);
      if (match) { setSelectedContactId(match.id); return; }
    }
  }, [contacts, effectiveExistingContact, virtualContact, extractedData, selectedContactId]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const addAnimation = (baseName) => {
    if (!baseName) return;
    if (selectedAnimations.some((e) => (e || "").toLowerCase() === baseName.toLowerCase())) return;
    const updated = [...selectedAnimations, baseName];
    updateAnimations(updated);
    setFormData(prev => ({
      ...prev,
      description: buildDescription(extractedData?.request_summary || prev.description, updated)
    }));
  };

  // Resolve the current selection against the catalog. Matching strategy:
  // SUBSTRING on the full reference (case-insensitive). Mirrors ExtractedDataPanel.
  const animationSelections = useMemo(() => {
    const allVariants = [];
    const refIndex = new Map();
    (animations || []).forEach((g) => {
      (g.variants || []).forEach((v) => {
        allVariants.push(v);
        if (v.reference) refIndex.set(v.reference.toLowerCase(), v);
      });
    });
    return selectedAnimations.map((entry) => {
      const key = (entry || "").toLowerCase().trim();
      const exact = refIndex.get(key);
      if (exact) {
        return {
          base_name: entry,
          sellsy_item_id: exact.sellsy_item_id,
          reference: exact.reference,
          variant_label: exact.variant_label,
          price: exact.price,
        };
      }
      const matches = key
        ? allVariants.filter((v) => (v.reference || "").toLowerCase().includes(key))
        : [];
      if (matches.length === 1) {
        const v = matches[0];
        return {
          base_name: entry,
          sellsy_item_id: v.sellsy_item_id,
          reference: v.reference,
          variant_label: v.variant_label,
          price: v.price,
        };
      }
      return {
        base_name: entry,
        sellsy_item_id: null,
        reference: null,
        variant_label: null,
        price: null,
      };
    });
  }, [selectedAnimations, animations]);

  const handleAnimationVariantChange = (idx, nextSelection) => {
    const next = [...selectedAnimations];
    next[idx] = nextSelection.reference || nextSelection.base_name;
    updateAnimations(next);
    setFormData(prev => ({
      ...prev,
      description: buildDescription(extractedData?.request_summary || prev.description, next)
    }));
  };

  const removeAnimationAt = (idx) => {
    const updated = selectedAnimations.filter((_, i) => i !== idx);
    updateAnimations(updated);
    setFormData(prev => ({
      ...prev,
      description: buildDescription(extractedData?.request_summary || prev.description, updated)
    }));
  };

  // Legacy single-arg remover, used by handler below in case other code paths
  // still call it. Removes the FIRST occurrence by name.
  // eslint-disable-next-line no-unused-vars
  const removeAnimation = (animName) => {
    const idx = selectedAnimations.indexOf(animName);
    if (idx >= 0) removeAnimationAt(idx);
  };

  const isVirtualId = (id) => typeof id === 'string' && (id.startsWith('new_contact_') || id.startsWith('new_company_'));

  // Build the payload using current form state (refs captured at submit time)
  const pendingPayloadRef = useRef(null);
  const { submit: submitOpportunity, dialogProps } = useDuplicateConfirm(async (force) => {
    if (!pendingPayloadRef.current) return null;
    const sep = force ? "?force=true" : "";
    return axios.post(`${apiUrl}/opportunities${sep}`, pendingPayloadRef.current);
  });

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name) {
      toast.error("Le nom de l'opportunite est obligatoire");
      return;
    }
    if (!selectedCompanyId) {
      toast.error("La societe est obligatoire");
      return;
    }
    if (!selectedContactId) {
      toast.error("Le contact est obligatoire");
      return;
    }
    if (isVirtualId(selectedCompanyId) || isVirtualId(selectedContactId)) {
      toast.error("Veuillez d'abord creer le contact/societe dans Sellsy via l'onglet Contact");
      return;
    }

    setLoading(true);
    try {
      const selectedStep = steps.find(s => s.id === selectedStepId);
      const selectedFunnel = funnels.find(f => f.id === selectedFunnelId);
      const selectedStaffMember = staff.find(s => s.id === selectedStaffId);

      const payload = {
        ...formData,
        company_id: selectedCompanyId,
        contact_id: selectedContactId,
        animations: selectedAnimations,
        funnelid: selectedFunnelId || null,
        stepid: selectedStepId || null,
        staffid: selectedStaffId && selectedStaffId !== 'none' ? selectedStaffId : null,
        pipeline_name: selectedFunnel?.name || null,
        step_name: selectedStep?.name || null,
        staff_name: selectedStaffMember?.name || null,
      };
      pendingPayloadRef.current = payload;      // submit() resolves with the created data, undefined if cancelled, or
      // { usedExisting: match } if user picked an existing Sellsy entity.
      const result = await submitOpportunity();
      if (!result) {
        toast.info("Création annulée");
        return;
      }
      if (result.usedExisting) {
        // Build a normalized opportunity object from the Sellsy match so the
        // QuoteForm (and dashboard) can use it as if we had just created it.
        const m = result.usedExisting;
        const reused = {
          id: m.sellsy_id,
          sellsy_id: m.sellsy_id,
          name: m.name || payload.name,
          amount: typeof m.amount === "object" ? m.amount?.value : m.amount,
          ...payload,
          // Preserve the existing Sellsy id over any local references
          sellsy_id_from_dup: true,
        };
        if (onOpportunityCreated) onOpportunityCreated(reused);
        toast.success(`Opportunité existante #${m.sellsy_id} sélectionnée`);
        return;
      }
      if (onOpportunityCreated) onOpportunityCreated(result);
      toast.success("Opportunite creee avec succes");
    } catch (error) {
      const detail = error.response?.data?.detail;
      const message = typeof detail === "string" ? detail : detail?.message || "Erreur lors de la creation";
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  // "Valider l'opportunité" — build the opportunity object locally from the
  // current form WITHOUT creating it in Sellsy. The quote can then be generated
  // directly from these data. Company & contact must still exist in Sellsy.
  const handleValidateOnly = () => {
    if (!formData.name) {
      toast.error("Le nom de l'opportunite est obligatoire");
      return;
    }
    if (!selectedCompanyId) {
      toast.error("La societe est obligatoire");
      return;
    }
    if (!selectedContactId) {
      toast.error("Le contact est obligatoire");
      return;
    }
    if (isVirtualId(selectedCompanyId) || isVirtualId(selectedContactId)) {
      toast.error("Veuillez d'abord creer le contact/societe dans Sellsy via l'onglet Contact");
      return;
    }
    const selectedStaffMember = staff.find(s => s.id === selectedStaffId);
    const validated = {
      validated_only: true,
      name: formData.name,
      amount: formData.amount,
      company_id: selectedCompanyId,
      contact_id: selectedContactId,
      animations: selectedAnimations,
      participants_count: formData.participants_count,
      event_location: formData.event_location,
      event_horaires: formData.event_horaires,
      event_date: formData.event_date,
      staff_name: selectedStaffMember?.name || null,
    };
    if (onOpportunityCreated) onOpportunityCreated(validated);
    toast.success("Opportunité validée (non créée dans Sellsy) — vous pouvez générer le devis");
  };

  const hasCompanies = companies.length > 0;
  const hasContacts = contacts.length > 0;
  const canCreate = hasCompanies && hasContacts;

  // Persisted opportunity success card (shown when an opportunity has been created for current email)
  const SuccessCard = createdOpportunity ? (
    <div className="bg-emerald-50 border border-emerald-200 rounded-md p-3 space-y-2" data-testid="opportunity-success-card">
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <p className="text-xs font-medium text-emerald-700 flex items-center gap-1">
            <CheckCircle2 className="w-3.5 h-3.5" /> {createdOpportunity.validated_only ? "Opportunité validée" : "Opportunité créée"}
          </p>
          <p className="text-sm text-emerald-900 font-medium truncate">{createdOpportunity.name}</p>
          <p className="text-xs text-emerald-600">
            {(createdOpportunity.amount || 0).toLocaleString('fr-FR')} EUR
            {createdOpportunity.sellsy_id && ` · Sellsy #${createdOpportunity.sellsy_id}`}
            {createdOpportunity.validated_only && ` · non créée dans Sellsy`}
          </p>
        </div>
        {createdOpportunity.sellsy_id && (
          <a
            href={`https://www.sellsy.com/prospection/opportunities/${createdOpportunity.sellsy_id}`}
            target="_blank"
            rel="noopener noreferrer"
            className="text-emerald-600 hover:text-emerald-800 flex-shrink-0 mt-0.5"
            title="Ouvrir l'opportunité dans Sellsy"
            data-testid="sellsy-opportunity-link"
          >
            <ExternalLink className="w-3.5 h-3.5" />
          </a>
        )}
      </div>
      <Button
        variant="secondary"
        size="sm"
        onClick={() => onOpportunityCreated && onOpportunityCreated(null)}
        className="w-full h-7 text-xs"
        data-testid="create-another-opportunity-btn"
      >
        Créer une autre opportunité
      </Button>
    </div>
  ) : null;

  if (createdOpportunity) {
    return SuccessCard;
  }

  if (!canCreate) {
    return (
      <div className="space-y-3" data-testid="opportunity-prerequisites">
        <div className="bg-amber-50 border border-amber-200 rounded-md p-3">
          <div className="flex items-start gap-2">
            <AlertCircle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-xs font-medium text-amber-800">Prerequis manquants</p>
              <p className="text-xs text-amber-700 mt-1">
                Pour creer une opportunite, vous devez d'abord avoir :
              </p>
              <ul className="text-xs text-amber-700 mt-2 space-y-1">
                <li className="flex items-center gap-2">
                  {hasCompanies ? <CheckCircle2 className="w-3 h-3 text-emerald-600" /> : <X className="w-3 h-3 text-amber-600" />}
                  <span className={hasCompanies ? "line-through text-amber-500" : ""}>Une societe creee</span>
                </li>
                <li className="flex items-center gap-2">
                  {hasContacts ? <CheckCircle2 className="w-3 h-3 text-emerald-600" /> : <X className="w-3 h-3 text-amber-600" />}
                  <span className={hasContacts ? "line-through text-amber-500" : ""}>Un contact cree</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-3" data-testid="opportunity-form">
      {/* Company Selection */}
      <div>
        <Label className="text-xs text-slate-600 flex items-center gap-1">
          <Building2 className="w-3 h-3" /> Societe (client) *
        </Label>
        <Select value={selectedCompanyId} onValueChange={setSelectedCompanyId}>
          <SelectTrigger className="h-8 text-sm" data-testid="opportunity-company-select">
            <SelectValue placeholder="Selectionner une societe" />
          </SelectTrigger>
          <SelectContent>
            {companies.map((company) => (
              <SelectItem key={company.id} value={company.id}>
                {company.name}{company.isVirtual ? " (a creer)" : ""}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Contact Selection */}
      <div>
        <Label className="text-xs text-slate-600 flex items-center gap-1">
          <User className="w-3 h-3" /> Contact lie *
        </Label>
        <Select value={selectedContactId} onValueChange={setSelectedContactId}>
          <SelectTrigger className="h-8 text-sm" data-testid="opportunity-contact-select">
            <SelectValue placeholder="Selectionner un contact" />
          </SelectTrigger>
          <SelectContent>
            {contacts.map((contact) => (
              <SelectItem key={contact.id} value={contact.id}>
                {contact.first_name} {contact.last_name} - {contact.email}
                {contact.isVirtual ? " (a creer)" : ""}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div>
        <Label className="text-xs text-slate-600">Nom de l'opportunite *</Label>
        <Input
          type="text"
          value={formData.name}
          onChange={(e) => handleChange("name", e.target.value)}
          className="h-8 text-sm"
          placeholder="Ex: ACME Corp - Team Building Mars 2026"
          required
          data-testid="opportunity-name-input"
        />
      </div>

      {/* Sellsy Fields: Pipeline, Status, Staff */}
      <div className="p-2.5 bg-slate-50 border border-slate-200 rounded-md space-y-2">
        <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Sellsy</p>

        <div>
          <Label className="text-xs text-slate-600 flex items-center gap-1">
            <GitBranch className="w-3 h-3" /> Pipeline
          </Label>
          <Select value={selectedFunnelId} onValueChange={setSelectedFunnelId}>
            <SelectTrigger className="h-8 text-sm" data-testid="opportunity-pipeline-select">
              <SelectValue placeholder="Pipeline" />
            </SelectTrigger>
            <SelectContent>
              {funnels.map((f) => (
                <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div>
          <Label className="text-xs text-slate-600">Statut</Label>
          <Select value={selectedStepId} onValueChange={setSelectedStepId}>
            <SelectTrigger className="h-8 text-sm" data-testid="opportunity-status-select">
              <SelectValue placeholder="Statut" />
            </SelectTrigger>
            <SelectContent>
              {steps.map((s) => (
                <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div>
          <Label className="text-xs text-slate-600 flex items-center gap-1">
            <Users className="w-3 h-3" /> Collaborateur affecte
          </Label>
          <Select value={selectedStaffId} onValueChange={setSelectedStaffId}>
            <SelectTrigger className="h-8 text-sm" data-testid="opportunity-staff-select">
              <SelectValue placeholder="Non affecte" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">Non affecte</SelectItem>
              {staff.map((s) => (
                <SelectItem key={s.id} value={s.id}>
                  {s.name}{s.email ? ` (${s.email})` : ''}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2">
        <div>
          <Label className="text-xs text-slate-600">Montant (EUR)</Label>
          <Input
            type="number"
            value={formData.amount}
            onChange={(e) => handleChange("amount", parseFloat(e.target.value) || 0)}
            className="h-8 text-sm"
            placeholder="0"
            data-testid="opportunity-amount-input"
          />
        </div>
        <div>
          <Label className="text-xs text-slate-600">Participants</Label>
          <Input
            type="number"
            value={formData.participants_count}
            onChange={(e) => handleChange("participants_count", parseInt(e.target.value) || 0)}
            className="h-8 text-sm"
            placeholder="0"
            data-testid="opportunity-participants-input"
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2">
        <div>
          <Label className="text-xs text-slate-600">Date début</Label>
          <Input
            type="date"
            value={formData.event_date}
            onChange={(e) => handleChange("event_date", e.target.value)}
            className="h-8 text-sm"
            data-testid="opportunity-date-input"
          />
        </div>
        <div>
          <Label className="text-xs text-slate-600">Date fin</Label>
          <Input
            type="date"
            value={formData.event_end_date}
            onChange={(e) => handleChange("event_end_date", e.target.value)}
            className="h-8 text-sm"
            data-testid="opportunity-end-date-input"
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2">
        <div>
          <Label className="text-xs text-slate-600">Horaires</Label>
          <Input
            type="text"
            value={formData.event_horaires}
            onChange={(e) => handleChange("event_horaires", e.target.value)}
            className="h-8 text-sm"
            placeholder="14h00 - 18h00"
            data-testid="opportunity-horaires-input"
          />
        </div>
        <div>
          <Label className="text-xs text-slate-600">Lieu</Label>
          <Input
            type="text"
            value={formData.event_location}
            onChange={(e) => handleChange("event_location", e.target.value)}
            className="h-8 text-sm"
            placeholder="Paris"
            data-testid="opportunity-location-input"
          />
        </div>
      </div>

      {/* Animation Selector — shared combobox + inline variant picker */}
      <div>
        <Label className="text-xs text-slate-600">Animations</Label>
        <AnimationCatalogCombobox
          animations={animations}
          selectedBaseNames={animationSelections.map((s) => s.base_name)}
          onAdd={addAnimation}
          label={null}
        />

        {animationSelections.length > 0 && (
          <div className="mt-2">
            <AnimationVariantPicker
              selections={animationSelections}
              animations={animations}
              onChange={handleAnimationVariantChange}
              onRemove={removeAnimationAt}
            />
          </div>
        )}
      </div>

      <div>
        <Label className="text-xs text-slate-600">Description</Label>
        <Textarea
          value={formData.description}
          onChange={(e) => handleChange("description", e.target.value)}
          className="text-sm min-h-[60px]"
          placeholder="Details de la demande..."
          data-testid="opportunity-description-input"
        />
      </div>

      <Button
        type="submit"
        disabled={loading || !selectedCompanyId || !selectedContactId}
        className="w-full h-8 text-xs bg-slate-900 hover:bg-slate-800"
        data-testid="create-opportunity-submit-btn"
      >
        {loading ? (
          <><Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> Creation...</>
        ) : (
          <><TrendingUp className="w-3.5 h-3.5 mr-1.5" /> Creer l'opportunite</>
        )}
      </Button>
      <Button
        type="button"
        variant="outline"
        onClick={handleValidateOnly}
        disabled={loading || !selectedCompanyId || !selectedContactId}
        className="w-full h-8 text-xs border-slate-300"
        data-testid="validate-opportunity-btn"
      >
        <CheckCircle2 className="w-3.5 h-3.5 mr-1.5" /> Valider sans créer dans Sellsy
      </Button>
      <p className="text-[10px] text-slate-400 text-center leading-snug">
        "Valider" récupère les infos pour le devis sans créer l'opportunité dans Sellsy
      </p>
      <DuplicateConfirmDialog {...dialogProps} />
    </form>
  );
};

export default OpportunityForm;
